/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useServiceContext } from '../../root'
import { DocumentsListPage } from './DocumentsListPage'
import { DOCUMENTS_VIEW_TYPE, DocumentsViewType } from './types'
import { SystemFolderType } from '../../constants/fsi'
import { PRESERVE_LAST_OPENED_FSI_ROLES } from '../../constants/user'
import { writeLastSelectedFolderId } from '../../utils/lastSelectedFolderStorage'

/**
 * AllFoldersDocumentsPage - Displays all folders and documents with filters and table functionality
 *
 * This page shows the regulated content root folder with:
 * - Filter panel (name, date range, state)
 * - Table view with sortable columns
 * - Breadcrumbs navigation
 * - Search functionality
 * - Grid/Table view toggle
 * - Action menus (actions will log to console)
 *
 * URL Parameters:
 * - folderId (optional): The folder ID to display. If not provided, shows regulated content root folder
 *
 * Routes:
 * - /app/browser - Redirects to root regulated content folder with ID in URL
 * - /app/browser/:folderId - Shows specific folder
 */
export const AllFoldersDocumentsPage: React.FC = () => {
  const context = useServiceContext()
  const { service } = context
  const currentRole = service.auth.getRole?.() || ''
  const shouldPreserveLastVisitedFolder = PRESERVE_LAST_OPENED_FSI_ROLES.has(currentRole)
  const navigate = useNavigate()
  const { folderId: folderIdParam } = useParams<{ folderId?: string }>()
  const [searchParams] = useSearchParams()
  const [folderId, setFolderId] = useState<string | undefined>(undefined)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!folderIdParam) {
      return
    }

    const numericFolderId = Number(folderIdParam)
    if (!Number.isFinite(numericFolderId) || numericFolderId <= 0) {
      return
    }

    if (shouldPreserveLastVisitedFolder) {
      writeLastSelectedFolderId(service.auth.getUser?.(), numericFolderId)
    }
  }, [folderIdParam, service.auth, shouldPreserveLastVisitedFolder])

  useEffect(() => {
    // Check if folderId is in URL path params
    if (folderIdParam) {
      // Always update when URL changes - this triggers re-render and data reload
      setFolderId(folderIdParam)
      setIsRedirecting(false)
    } else {
      // No folderId in URL - get the regulated content root folder and redirect
      const regulatedContentFolder = service.auth.getBrowser(SystemFolderType.REGULATED_CONTENT)

      if (regulatedContentFolder && regulatedContentFolder.id) {
        const folderIdStr = String(regulatedContentFolder.id)
        setIsRedirecting(true)
        // Redirect to the URL with folderId
        navigate(`/browser/${folderIdStr}`, { replace: true })
      } else {
        // Show an error message instead of redirecting to an arbitrary folder
        setError('Unable to determine the regulated content root folder. Please verify your access or try again later.')
        setIsRedirecting(false)
      }
    }
  }, [service.auth, folderIdParam, navigate]) // Dependency on folderIdParam ensures re-run when URL changes

  // Show loading state while fetching folder ID or redirecting
  if (error) {
    return (
      <div className="page-frame">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    )
  }

  if (!folderId || isRedirecting) {
    return (
      <div className="page-frame">
        <div className="loading-spinner">
          <i className="fa fa-spinner fa-spin fa-3x" />
        </div>
      </div>
    )
  }

  // Determine viewType based on type query parameter for system folders
  const typeParam = searchParams.get('type')
  let viewType: DocumentsViewType = DOCUMENTS_VIEW_TYPE.regulatedContent

  // Check if there's a type parameter (for both abstract and real system folders)
  if (typeParam) {
    // Map system folder types to appropriate viewType
    switch (typeParam) {
      case SystemFolderType.SYSTEM_DOCUMENTS:
      case SystemFolderType.EMAIL_UPLOADS:
      case SystemFolderType.DOCUMENT_MIGRATIONS_AND_EXPORTS:
      case SystemFolderType.REPORTS:
      case SystemFolderType.SETTINGS_AND_SYSTEM_EXPORTS_IMPORTS:
      case SystemFolderType.FORMS:
      case SystemFolderType.TEMPLATES:
      case SystemFolderType.INIT_ETMF_FILES:
      case SystemFolderType.WORKFLOWS:
        viewType = typeParam as DocumentsViewType
        break
      default:
        viewType = DOCUMENTS_VIEW_TYPE.regulatedContent
    }
  }

  return (
    <DocumentsListPage
      key={`${folderId}-${typeParam || 'regular'}`} // Force re-mount when folderId or type changes
      viewType={viewType}
      folderId={folderId}
      initialPage={1}
    />
  )
}
